import { cn } from "@/lib/utils"

// Animated "agent orb" — adapted from a styled-jsx snippet to plain CSS so it
// runs under Vite. Colors are CSS variables so a partner theme can rebrand it.

interface SiriOrbProps {
  size?: number
  className?: string
  colors?: {
    bg?: string
    c1?: string
    c2?: string
    c3?: string
  }
  animationDuration?: number
}

const ORB_CSS = `
@property --angle {
  syntax: "<angle>";
  inherits: false;
  initial-value: 0deg;
}

.siri-orb {
  display: grid;
  grid-template-areas: "stack";
  overflow: hidden;
  border-radius: 50%;
  position: relative;
  background: radial-gradient(
    circle,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0.03) 30%,
    transparent 70%
  );
}

.dark .siri-orb {
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0.02) 30%,
    transparent 70%
  );
}

.siri-orb::before {
  content: "";
  display: block;
  grid-area: stack;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background:
    conic-gradient(
      from calc(var(--angle) * 1.2) at 30% 65%,
      var(--c3) 0deg,
      transparent 45deg 315deg,
      var(--c3) 360deg
    ),
    conic-gradient(
      from calc(var(--angle) * 0.8) at 70% 35%,
      var(--c2) 0deg,
      transparent 60deg 300deg,
      var(--c2) 360deg
    ),
    conic-gradient(
      from calc(var(--angle) * -1.5) at 65% 75%,
      var(--c1) 0deg,
      transparent 90deg 270deg,
      var(--c1) 360deg
    ),
    conic-gradient(
      from calc(var(--angle) * 2.1) at 25% 25%,
      var(--c2) 0deg,
      transparent 30deg 330deg,
      var(--c2) 360deg
    ),
    conic-gradient(
      from calc(var(--angle) * -0.7) at 80% 80%,
      var(--c1) 0deg,
      transparent 45deg 315deg,
      var(--c1) 360deg
    ),
    radial-gradient(
      ellipse 120% 80% at 40% 60%,
      var(--c3) 0%,
      transparent 50%
    );
  filter: blur(var(--blur-amount)) contrast(var(--contrast-amount)) saturate(1.2);
  animation: siri-orb-rotate var(--animation-duration) linear infinite;
  transform: translateZ(0);
  will-change: transform;
}

.siri-orb::after {
  content: "";
  display: block;
  grid-area: stack;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(
    circle at 45% 55%,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 30%,
    transparent 60%
  );
  mix-blend-mode: overlay;
}

@keyframes siri-orb-rotate {
  from {
    --angle: 0deg;
  }
  to {
    --angle: 360deg;
  }
}

@media (prefers-reduced-motion: reduce) {
  .siri-orb::before {
    animation: none;
  }
}
`

export function SiriOrb({
  size = 192,
  className,
  colors,
  animationDuration = 20,
}: SiriOrbProps) {
  const finalColors = {
    bg: "transparent",
    c1: "oklch(66% 0.19 255)",
    c2: "oklch(82% 0.1 220)",
    c3: "oklch(72% 0.16 275)",
    ...colors,
  }

  const blurAmount = Math.max(size * 0.08, 8)
  const contrastAmount = Math.max(size * 0.003, 1.8)

  return (
    <>
      <style>{ORB_CSS}</style>
      <div
        className={cn("siri-orb", className)}
        style={
          {
            width: `${size}px`,
            height: `${size}px`,
            "--bg": finalColors.bg,
            "--c1": finalColors.c1,
            "--c2": finalColors.c2,
            "--c3": finalColors.c3,
            "--animation-duration": `${animationDuration}s`,
            "--blur-amount": `${blurAmount}px`,
            "--contrast-amount": contrastAmount,
          } as React.CSSProperties
        }
      />
    </>
  )
}
